// src/pages/locations/components/form/FormField.tsx
import React from "react";
import { FiAlertCircle } from "react-icons/fi";

/**
 * Field chrome shared by every Locations input: label, requirement marker,
 * hint, and the error slot. Hint and error are wired to the control through
 * aria-describedby so screen readers announce them with the field.
 */
export const FormField: React.FC<{
  id: string;
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string | null;
  children: (props: {
    id: string;
    "aria-invalid": boolean;
    "aria-describedby": string | undefined;
  }) => React.ReactNode;
}> = ({ id, label, required = false, hint, error, children }) => {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-xs font-semibold text-gray-700"
      >
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children({
        id,
        "aria-invalid": Boolean(error),
        "aria-describedby": describedBy,
      })}

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-red-600"
        >
          <FiAlertCircle className="shrink-0" />
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
          {hint}
        </p>
      )}
    </div>
  );
};
